import type { Selection } from "../../charts/utils";
import { createNavColors, LEVEL1_COLOR_OFFSET_BY_SYSTEM, LEVEL3_COLOR_RANK_YEAR } from "../../colors";
import transformedDataRaw from "../../data/emissions-data.json";

export type AreaKey = "area1" | "area2" | "area3" | "area4" | "area5";

export interface AreaNavItem {
    key: string;
    label: string;
    area: AreaKey;
    fill: string;
    hoverFill: string;
    stroke?: string;
    hoverStroke?: string;
    icon?: React.ReactNode;
}

export interface LevelConfig {
    data: AreaData[];
    navItems: AreaNavItem[];
    activeAreas: AreaKey[];
}

export type AreaData = {
    year: number;
    area1: number;
    area2: number;
    area3: number;
    area4: number;
    area5: number;
    utenEnova: number;
    klimamal: number;
};

// Type the imported data
type TransformedData = {
    [system: string]: {
        level1: AreaData[];
        level2: { [key: string]: AreaData[] };
        level3: { [key: string]: AreaData[] };
        nivel1Keys: string[];
    };
};
const transformedData = transformedDataRaw as TransformedData;

const getNivel2KeysSortedByLevel3Rank = (
    nivel2Keys: string[],
    nivel1Key: string,
    combinedLevel3Data: AreaData[],
): string[] => {
    const yearData = combinedLevel3Data.find((item) => item.year === LEVEL3_COLOR_RANK_YEAR);
    if (!yearData) {
        return nivel2Keys;
    }

    return [...nivel2Keys].sort((keyA, keyB) => {
        const idxA = nivel2Keys.indexOf(keyA);
        const idxB = nivel2Keys.indexOf(keyB);
        const areaKeyA = `area${idxA + 1}` as AreaKey;
        const areaKeyB = `area${idxB + 1}` as AreaKey;
        const diff = (yearData[areaKeyB] ?? 0) - (yearData[areaKeyA] ?? 0);
        if (diff !== 0) {
            return diff;
        }

        const labelA = keyA.replace(`${nivel1Key}_`, "");
        const labelB = keyB.replace(`${nivel1Key}_`, "");
        return labelA.localeCompare(labelB, "nb-NO");
    });
};

// Get all available systems (Godstransport, Persontransport, etc.)
export const availableSystems = Object.keys(transformedData);

// Extended data - combine all systems for overview
// For level 1, each system's total goes into a separate area
const createLevel1Data = (): AreaData[] => {
    // Get years from first system
    const firstSystem = transformedData[availableSystems[0]];
    if (!firstSystem) return [];

    return firstSystem.level1.map((yearData) => {
        const combined: AreaData = {
            year: yearData.year,
            area1: 0,
            area2: 0,
            area3: 0,
            area4: 0,
            area5: 0,
            utenEnova: 0,
            klimamal: 0,
        };

        // For each system, calculate its total and assign to corresponding area
        availableSystems.forEach((system, idx) => {
            if (idx < 5) {
                const systemYearData = transformedData[system].level1.find((d) => d.year === yearData.year);
                if (systemYearData) {
                    const areaKey = `area${idx + 1}` as AreaKey;
                    // Sum all areas from this system
                    const systemTotal =
                        (systemYearData.area1 ?? 0) +
                        (systemYearData.area2 ?? 0) +
                        (systemYearData.area3 ?? 0) +
                        (systemYearData.area4 ?? 0) +
                        (systemYearData.area5 ?? 0);
                    combined[areaKey] = systemTotal;
                    combined.utenEnova += systemYearData.utenEnova ?? 0;
                    combined.klimamal += systemYearData.klimamal ?? 0;
                }
            }
        });

        return combined;
    });
};

export const extendedData: AreaData[] = createLevel1Data();

// Helper function to format area and system names for display
export function getAreaDisplayName(areaKey: AreaKey, system: string): string {
    if (system && transformedData[system]) {
        const numIndex = parseInt(areaKey.replace("area", ""), 10) - 1;
        if (numIndex >= 0 && numIndex < transformedData[system].nivel1Keys.length) {
            return transformedData[system].nivel1Keys[numIndex];
        }
    }
    return areaKey;
}

// Level 1 configuration - shows systems
const level1Config: LevelConfig = {
    data: extendedData,
    navItems: availableSystems.map((system, idx) => {
        const level1Offset = LEVEL1_COLOR_OFFSET_BY_SYSTEM[system] ?? 0;
        const { fill, hoverFill, stroke, hoverStroke } = createNavColors(system, level1Offset);
        return {
            key: system,
            label: system,
            area: `area${(idx % 5) + 1}` as AreaKey,
            fill,
            hoverFill,
            stroke,
            hoverStroke,
        };
    }),
    activeAreas: ["area1", "area2"],
};

// Level 2 configurations - shows categories within a system
const level2Configs: Record<string, LevelConfig> = {};

availableSystems.forEach((system) => {
    const systemData = transformedData[system];
    const nivel1Keys = systemData.nivel1Keys;

    const level2Data = systemData.level1.map((item) => ({ ...item }));

    level2Configs[system] = {
        data: level2Data,
        navItems: nivel1Keys.map((name, idx) => {
            const colors = createNavColors(system, idx);
            return {
                key: name,
                label: name,
                area: `area${idx + 1}` as AreaKey,
                fill: colors.fill,
                hoverFill: colors.hoverFill,
                stroke: colors.stroke,
                hoverStroke: colors.hoverStroke,
            };
        }),
        activeAreas: nivel1Keys.map((_, idx) => `area${idx + 1}`) as AreaKey[],
    };
});

// Level 3 configurations - shows details within a category
const level3Configs: Record<string, LevelConfig> = {};

availableSystems.forEach((system) => {
    const systemData = transformedData[system];
    const nivel1Keys = systemData.nivel1Keys;

    nivel1Keys.forEach((nivel1Key, level1Idx) => {
        const configKey = `${system}_${level1Idx}`;
        const nivel2Keys = Object.keys(systemData.level3).filter((k) => k.startsWith(`${nivel1Key}_`));

        // Combine level3 data for this category
        const combinedLevel3Data: AreaData[] = [];
        if (nivel2Keys.length > 0 && systemData.level3[nivel2Keys[0]]) {
            // Initialize data structure based on first level3 entry
            const firstEntry = systemData.level3[nivel2Keys[0]];
            firstEntry.forEach((item, idx) => {
                combinedLevel3Data[idx] = {
                    year: item.year,
                    area1: 0,
                    area2: 0,
                    area3: 0,
                    area4: 0,
                    area5: 0,
                    utenEnova: 0,
                    klimamal: 0,
                };
            });

            // Fill in area values and accumulate utenEnova from each level3 item
            // For klimamal, use proportional share of parent klimamal
            nivel2Keys.forEach((key, areaIdx) => {
                if (areaIdx < 5 && systemData.level3[key]) {
                    const areaKey = `area${areaIdx + 1}` as AreaKey;

                    systemData.level3[key].forEach((item, yearIdx) => {
                        if (combinedLevel3Data[yearIdx]) {
                            combinedLevel3Data[yearIdx][areaKey] = item.area1 || 0;
                            combinedLevel3Data[yearIdx].utenEnova += item.utenEnova || 0;
                        }
                    });
                }
            });

            firstEntry.forEach((_, yearIdx) => {
                if (!combinedLevel3Data[yearIdx]) {
                    return;
                }
                const summedKlimamal = nivel2Keys.reduce((sum, key) => {
                    const subItem = systemData.level3[key]?.[yearIdx];
                    return sum + (subItem?.klimamal ?? 0);
                }, 0);
                combinedLevel3Data[yearIdx].klimamal = summedKlimamal;
            });
        }

        level3Configs[configKey] = {
            data: combinedLevel3Data,
            navItems: nivel2Keys.map((key, idx) => {
                const colors = createNavColors(system, idx);
                return {
                    key,
                    label: key.replace(`${nivel1Key}_`, ""),
                    area: `area${idx + 1}` as AreaKey,
                    fill: colors.fill,
                    hoverFill: colors.hoverFill,
                    stroke: colors.stroke,
                    hoverStroke: colors.hoverStroke,
                };
            }),
            activeAreas: nivel2Keys.slice(0, 5).map((_, idx) => `area${idx + 1}`) as AreaKey[],
        };
    });
});

// Level 4 configurations - shows details of a single level 3 item (drill-down view)
const level4Configs: Record<string, LevelConfig> = {};

availableSystems.forEach((system) => {
    const systemData = transformedData[system];
    const nivel1Keys = systemData.nivel1Keys;
    const level3DataByCategory: Record<string, AreaData[]> = {};

    // Build a lookup for level 3 data by category key
    nivel1Keys.forEach((nivel1Key, level1Idx) => {
        const configKey = `${system}_${level1Idx}`;
        const nivel2Keys = Object.keys(systemData.level3).filter((k) => k.startsWith(`${nivel1Key}_`));
        const combinedLevel3Data: AreaData[] = [];
        if (nivel2Keys.length > 0 && systemData.level3[nivel2Keys[0]]) {
            const firstEntry = systemData.level3[nivel2Keys[0]];
            firstEntry.forEach((item, idx) => {
                combinedLevel3Data[idx] = {
                    year: item.year,
                    area1: 0,
                    area2: 0,
                    area3: 0,
                    area4: 0,
                    area5: 0,
                    utenEnova: 0,
                    klimamal: 0,
                };
            });
            // Fill in area values and accumulate utenEnova from each level3 item
            // For klimamal, use proportional share of parent klimamal
            nivel2Keys.forEach((key, areaIdx) => {
                if (areaIdx < 5 && systemData.level3[key]) {
                    const areaKey = `area${areaIdx + 1}` as AreaKey;

                    systemData.level3[key].forEach((item, yearIdx) => {
                        if (combinedLevel3Data[yearIdx]) {
                            combinedLevel3Data[yearIdx][areaKey] = item.area1 || 0;
                            combinedLevel3Data[yearIdx].utenEnova += item.utenEnova || 0;
                        }
                    });
                }
            });

            firstEntry.forEach((_, yearIdx) => {
                if (!combinedLevel3Data[yearIdx]) {
                    return;
                }
                const summedKlimamal = nivel2Keys.reduce((sum, key) => {
                    const subItem = systemData.level3[key]?.[yearIdx];
                    return sum + (subItem?.klimamal ?? 0);
                }, 0);
                combinedLevel3Data[yearIdx].klimamal = summedKlimamal;
            });
        }

        level3DataByCategory[configKey] = combinedLevel3Data;
    });

    nivel1Keys.forEach((nivel1Key, level1Idx) => {
        const nivel2Keys = Object.keys(systemData.level3).filter((k) => k.startsWith(`${nivel1Key}_`));
        const level3ConfigKey = `${system}_${level1Idx}`;
        const level3SortedKeys = getNivel2KeysSortedByLevel3Rank(
            nivel2Keys,
            nivel1Key,
            level3DataByCategory[level3ConfigKey] ?? [],
        );
        // nivel2Keys.forEach((nivel2Key) => { // keep this commented code
        nivel2Keys.forEach((nivel2Key, areaIdx) => {
            const configKey = `${system}_${level1Idx}_${nivel2Key}`;
            // Get data for this specific level3 item
            const level4Data: AreaData[] = [];
            if (systemData.level3[nivel2Key]) {
                systemData.level3[nivel2Key].forEach((item) => {
                    level4Data.push({
                        year: item.year,
                        area1: item.area1 || 0,
                        area2: 0,
                        area3: 0,
                        area4: 0,
                        area5: 0,
                        utenEnova: item.utenEnova || 0,
                        klimamal: item.klimamal || 0,
                    });
                });
            }
            const label = nivel2Key.replace(`${nivel1Key}_`, "");
            // const level1Offset = level1OffsetBySystem[system] ?? 0; // keep this commented code for reference if we want to use the same colors as level 1 for level 4 items (currently we use area index to determine color, which means all items under the same area have the same color regardless of system)
            // const colors = createNavColors(system, level1Offset); // keep this commented code
            const rankIdx = level3SortedKeys.indexOf(nivel2Key);
            const colorOffset = rankIdx === -1 ? areaIdx : rankIdx;
            const colors = createNavColors(system, colorOffset);
            level4Configs[configKey] = {
                data: level4Data,
                navItems: [
                    {
                        key: nivel2Key,
                        label,
                        area: "area1" as AreaKey,
                        fill: colors.fill,
                        hoverFill: colors.hoverFill,
                        stroke: colors.stroke,
                        hoverStroke: colors.hoverStroke,
                    },
                ],
                activeAreas: ["area1"],
            };
        });
    });
});

// --- KLIMAMÅL CALCULATION LOGIC ---
// Level 1: bruker de aggregerte måltallene for hvert system (sum i extendedData).
// Level 2: benytter klimamålene som allerede følger hvert system i transformert data.
// Level 3: summerer klimamålene for underkategoriene som vises i valgt kategori.
// Level 4: viser klimamålet for den valgte underkategorien direkte.
// --- END KLIMAMÅL LOGIC ---

// Main function to get configuration for any level
export function getLevelConfig(
    level: number,
    selection: {
        level1: string | null;
        level2: string | null;
        level3: string | null;
        level4?: string | null;
    },
): LevelConfig {
    if (level === 1) {
        return level1Config;
    } else if (level === 2 && selection.level1) {
        return level2Configs[selection.level1] || level1Config;
    } else if (level === 3 && selection.level1 && selection.level2) {
        const configKey = `${selection.level1}_${transformedData[selection.level1]?.nivel1Keys.indexOf(selection.level2) || 0}`;
        return level3Configs[configKey] || level1Config;
    } else if (level === 4 && selection.level1 && selection.level2 && selection.level3) {
        const configKey = getLevel4SelectionKey({
            level1: selection.level1,
            level2: selection.level2,
            level3: selection.level3,
            level4: selection.level4 ?? null,
        });
        if (configKey) {
            return level4Configs[configKey] || level1Config;
        }
    }
    return level1Config;
}

export function getLevel4SelectionKey(selection: Selection): string | null {
    if (!selection.level1 || !selection.level2 || !selection.level3) {
        return null;
    }
    const nivel1Keys = transformedData[selection.level1]?.nivel1Keys;
    if (!nivel1Keys) {
        return null;
    }
    const level1Idx = nivel1Keys.indexOf(selection.level2);
    if (level1Idx === -1) {
        return null;
    }
    return `${selection.level1}_${level1Idx}_${selection.level3}`;
}
